/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { useState } from 'react';
import { AlertColor } from '@mui/material';
import MasterDataApiService from '../../../../../../Shared/Services/MasterDataService';

export const useTaskData = (userId: string, showSnackbar: (severity: AlertColor, message: string) => void) => {
  const [taskOptions, setTaskOptions] = useState<{ label: string; value: string; subTasks: []; tasks: any }[]>([]);
  const [availableTasksData, setAvailableTasksData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [subTasksObj, setSubTasksObj] = useState<any>();
  const [mainTasksObj, setMainTasksObj] = useState<any>();

  const fetchAllTasks = async (pageNumber: number, searchValue: string = '') => {
    try {
      const allTasksResponse = await MasterDataApiService.getAllTasks(userId, pageNumber, 10, searchValue);
      if (allTasksResponse?.data) {
        const tasksOptions = allTasksResponse?.data.map(
          (task: { taskName: string; mdTaskId: string; subTasks: []; tasks: any }) => ({
            label: task.taskName,
            value: task.mdTaskId,
            subTasks: task.subTasks,
            tasks: task,
          })
        );
        setTaskOptions(tasksOptions);
        setTotalCount(allTasksResponse?.metaData?.total || 0);
        return tasksOptions;
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      showSnackbar('error', 'Failed to fetch tasks');
    }
    return [];
  };

  const getServiceCategories = async (customerId: string) => {
    try {
      const allServiceCategoryV2Response = await MasterDataApiService.getAllServiceCategoryV3(customerId);
      if (allServiceCategoryV2Response?.data) {
        setAvailableTasksData(allServiceCategoryV2Response?.data);
        return allServiceCategoryV2Response?.data?.map((categoryItem: any) => {
          return {
            categoryId: categoryItem?.serviceCategoryId,
            categoryName: categoryItem?.serviceCategoryName,
            unit: categoryItem?.unit,
          };
        });
      }
    } catch (error) {
      console.error('Error fetching service categories:', error);
      showSnackbar('error', 'Failed to fetch service categories');
    }
    return [];
  };

  const handleTaskSelection = (
    taskValue: string,
    setTaskSubTasks: (tasks: any) => void,
    setTaskConfirmed: (confirmed: boolean) => void
  ) => {
    const selectedTaskObj = taskOptions.find((t) => t.value === taskValue);
    setTaskSubTasks(selectedTaskObj?.subTasks || []);
    setMainTasksObj(selectedTaskObj?.tasks);
    setTaskConfirmed(true);
    return selectedTaskObj;
  };

  const handleSubTaskSelection = (subTaskValue: string, setSubTaskConfirmed: (confirmed: boolean) => void) => {
    const selectedSubTasks = taskOptions.find((t) => t.value === subTaskValue);
    setSubTasksObj(selectedSubTasks?.tasks);
    setSubTaskConfirmed(true);
    return selectedSubTasks;
  };

  return {
    taskOptions,
    setTaskOptions,
    availableTasksData,
    setAvailableTasksData,
    totalCount,
    setTotalCount,
    subTasksObj,
    setSubTasksObj,
    mainTasksObj,
    setMainTasksObj,

    fetchAllTasks,
    getServiceCategories,
    handleTaskSelection,
    handleSubTaskSelection,
  };
};
